import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import useIsFlaggedRegion from '@/hooks/useIsFlaggedRegion';
import catBetterPackagesIcon from '@/static/assets/pet/cat/bettercare_packages_icon.svg';
import catBetterPackagesIconMobile from '@/static/assets/pet/cat/bettercare_packages_icon_mobile.svg';
import betterCancelIcon from '@/static/assets/pet/dog/bettercare_cancel_icon.svg';
import betterCancelIconMobile from '@/static/assets/pet/dog/bettercare_cancel_icon_mobile.svg';
import betterHealthyIcon from '@/static/assets/pet/dog/bettercare_healthy_icon.svg';
import betterHealthyIconMobile from '@/static/assets/pet/dog/bettercare_healthy_icon_mobile.svg';
import petBetterPackagesIcon from '@/static/assets/pet/dog/bettercare_packages_icon.svg';
import petBetterPackagesIconMobile from '@/static/assets/pet/dog/bettercare_packages_icon_mobile.svg';
import betterVetsIcon from '@/static/assets/pet/dog/bettercare_vets_icon.svg';
import betterVetsIconMobile from '@/static/assets/pet/dog/bettercare_vets_icon_mobile.svg';
import quote from '@/static/assets/pet/quote_sign.svg';
import { PRODUCT_PET } from '@/utils/products';
import { Flex, ResponsiveBreak, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import type React from 'react';
import styled from 'styled-components';
import { PetLandingPageType } from './shared';

const TakeBetterCareWrapper = styled(Section).attrs({ darkBackground: true })`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;
  text-transform: capitalize;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

export const MainCTA = styled(JoinCTA)`
  margin-bottom: 50px;
  margin-top: 18px;
  font-size: 1.6rem;
  width: 197px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 64px;
        width: 288px;
        margin-top: 40px;
    `}
`;

const Offers = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 32px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        flex-direction: row;
        max-width: 1140px;
        flex-wrap: wrap;
    `}
`;

const OfferListItem = styled(Flex)`
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 auto;

  ${MEDIA_QUERIES.tabletAndDesktop`
        flex-direction: column;
        align-items: center;
    `}
`;

const IllustrationWrapper = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  background-color: transparent;
  flex-shrink: 0;

  & > img {
    max-width: 100%;
    object-fit: cover;
  }

  & > img[data-display='desktop'] {
    display: none;
  }

  & > img[data-display='mobile'] {
    display: block;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        width: 175px;
        height: 175px;

        & > img[data-display='desktop'] {
            display: block;
        }

        & > img[data-display='mobile'] {
            display: none;
        }
    `}
`;

const ContentWrapper = styled.div`
  width: 100%;

  ${MEDIA_QUERIES.tabletAndDesktop`
        max-width: 250px;
    `}
`;

const OfferHeading = styled.p`
  ${font('main', { fontSize: '1.8rem', lineHeight: '1.2', fontWeight: 700 })};
  text-align: revert;
  text-transform: capitalize;
  margin-bottom: 8px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        font-size: 2rem;
        text-align: center;
        margin-bottom: 16px;
    `}
`;

const OfferDescription = styled.p`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
  text-align: revert;
  color: ${themedColor('dark')};
  width: 88%;

  ${MEDIA_QUERIES.tabletAndDesktop`
        font-size: 1.6rem;
        text-align: center;
        width: initial;
    `}
`;

const Quote = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'center',
})`
  width: 100%;

  ${MEDIA_QUERIES.tablet`
        width: 80%;
    `}

  ${MEDIA_QUERIES.desktop`
        max-width: 800px;
    `}
`;

const Cite = styled(Flex)`
  ${font('main', { fontSize: '2rem', lineHeight: '1.5' })};
  align-items: center;
  color: ${themedColor('dark')};
  margin-bottom: 16px;
  flex-direction: column;
  gap: 10px;

  & > span {
    text-align: center;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        flex-direction: row;
        align-items: flex-start;
        ${font('main', { fontSize: '2.4rem', lineHeight: '1.5' })};
    `}
`;

const Origin = styled.p`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
  text-align: center;
  text-transform: capitalize;
  color: ${themedColor('night')};
  font-weight: 700 !important;

  ${MEDIA_QUERIES.tabletAndDesktop`
        ${font('main', { fontSize: '1.6rem', lineHeight: '1.5' })};
    `}
`;
type StringOrStringGetter = string | ((petLandingPageType: PetLandingPageType) => string);

const HEALTHY_DESCRIPTION = {
  [PetLandingPageType.Dog]:
    'Don’t wait for something bad to happen. Get insured today, and be protected for the future.',
  [PetLandingPageType.Cat]: 'Don’t wait for something to happen, maximize your coverage by getting insured now.',
  [PetLandingPageType.PetInsurance]:
    'Don’t wait for something to happen, maximize your coverage by getting insured now',
};

const CANCEL_DESCRIPTION = {
  [PetLandingPageType.Dog]:
    'Changed your mind? Get a refund if you cancel within the first 30 days. Just know that waiting periods will reset.',
  [PetLandingPageType.Cat]:
    'Cancel on the Lemonade app. Get refunded if it’s within the first 30 days. Just know that waiting periods will reset.',
  [PetLandingPageType.PetInsurance]:
    'Cancel on the Lemonade app. Get refunded if it’s within the first 30 days. Just know that waiting periods will reset.',
};

const VET_DESCRIPTION = {
  [PetLandingPageType.Dog]: 'Choose Any Vet',
  [PetLandingPageType.Cat]: 'All Vets Welcome',
  [PetLandingPageType.PetInsurance]: 'All Vets Welcome',
};

const PUPPY_KITTEN_PACKAGE = {
  [PetLandingPageType.Dog]: 'Pick a puppy package',
  [PetLandingPageType.Cat]: 'Get a kitten package',
  [PetLandingPageType.PetInsurance]: 'Puppy/Kitten Package',
};

const title = {
  [PetLandingPageType.Dog]: 'Show Your Dog Some Love',
  [PetLandingPageType.Cat]: 'Your Cat’s Care, Simplified',
  [PetLandingPageType.PetInsurance]: (
    <>
      Make Your Pet’s
      <ResponsiveBreak display={['block', 'inline']} />
      Day Today
    </>
  ),
};

const getOffers = (
  isFlaggedRegion: boolean,
  petLandingPageType: PetLandingPageType,
): {
  title: StringOrStringGetter;
  description: string;
  iconImgSrc: StaticImageData;
  iconImgMobileSrc: StaticImageData;
}[] => [
  {
    title: 'start when they’re healthy',
    description: HEALTHY_DESCRIPTION[petLandingPageType],
    iconImgSrc: betterHealthyIcon,
    iconImgMobileSrc: betterHealthyIconMobile,
  },
  {
    title: VET_DESCRIPTION[petLandingPageType],
    description: 'Go to any vet in the United States. As long as they’re licensed to provide vet care in their state.',
    iconImgSrc: betterVetsIcon,
    iconImgMobileSrc: betterVetsIconMobile,
  },
  {
    title: PUPPY_KITTEN_PACKAGE[petLandingPageType],
    description: 'Covers procedures like spaying or neutering, microchipping, six vaccinations, and more!',
    iconImgSrc: petLandingPageType === PetLandingPageType.Cat ? catBetterPackagesIcon : petBetterPackagesIcon,
    iconImgMobileSrc:
      petLandingPageType === PetLandingPageType.Cat ? catBetterPackagesIconMobile : petBetterPackagesIconMobile,
  },
  {
    title: 'Cancel at any time',
    description: isFlaggedRegion
      ? `Cancel from your phone on the Lemonade app. Just know that waiting periods will reset.
        `
      : CANCEL_DESCRIPTION[petLandingPageType],
    iconImgSrc: betterCancelIcon,
    iconImgMobileSrc: betterCancelIconMobile,
  },
];

interface TakeBetterCareProps {
  petLandingPageType: PetLandingPageType;
}

const TakeBetterCare: React.FC<TakeBetterCareProps> = ({ petLandingPageType }) => {
  const isFlaggedRegion = useIsFlaggedRegion();
  return (
    <TakeBetterCareWrapper justifyContent="center" py={[40, 70]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>{title[petLandingPageType]}</Heading>
        {petLandingPageType === PetLandingPageType.Cat ? (
          <SubHeading>
            Get that ball of yarn rolling
            <ResponsiveBreak display={['block', 'inline']} />
            towards total coverage
          </SubHeading>
        ) : (
          <SubHeading>
            The sooner you protect them, the
            <ResponsiveBreak display={['block', 'inline']} />
            more they’re protected against
          </SubHeading>
        )}
      </Flex>
      <Offers py={[32, 32, 0]} px={[16, 16, 0]}>
        {getOffers(isFlaggedRegion, petLandingPageType).map(({ title, description, iconImgSrc, iconImgMobileSrc }) => (
          <OfferListItem key={description}>
            <IllustrationWrapper>
              <Image data-display="desktop" src={iconImgSrc} alt={description} />
              <Image data-display="mobile" src={iconImgMobileSrc} alt={description} width={80} height={80} />
            </IllustrationWrapper>
            <ContentWrapper>
              <OfferHeading>{typeof title === 'string' ? title : title(petLandingPageType)}</OfferHeading>
              <OfferDescription>{description}</OfferDescription>
            </ContentWrapper>
          </OfferListItem>
        ))}
      </Offers>
      <MainCTA
        size="big"
        flavor="primary"
        glowing
        data-source="pet"
        product={PRODUCT_PET}
        analyticsParams={{
          position: 'center',
          pet_lp_type: petLandingPageType,
        }}
      >
        check our prices
      </MainCTA>
      <Quote px={[32, 32, 0]}>
        <Cite>
          <Image alt="quotation mark" src={quote} />
          <span>
            Instead of rushing over when something bad happens, people should bring their pets in to keep them healthy
            as they mature.
          </span>
        </Cite>
        <Origin> Dr. Stephanie Liff, DVM, Lemonade’s vet expert</Origin>
      </Quote>
    </TakeBetterCareWrapper>
  );
};

export default TakeBetterCare;
